import { useState } from "react";
import { ArrowRight, MoreHorizontal, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import type { BoardLead, LeadStage } from "@school-sales-ai/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteLeadDialog } from "@/components/DeleteLeadDialog";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";
import { nextStages } from "@/lib/board-drag";
import { useBoardMove } from "@/components/leads-pipeline/board-move-context";
import { AssigneeSubMenu } from "@/components/lead-assignment/AssigneeSubMenu";
import { useUpdateLead } from "@/hooks/useLeads";
import { useCrmStore } from "@/stores/crm.store";

type PaletteColor = (typeof PIPELINE_COLOR_OPTIONS)[number]["name"];

type Props = {
  leadId: string;
  leadName: string | null;
  color: BoardLead["color"];
  stage: LeadStage;
  assignedProfileId: string | null;
};

export function KebabMenu({
  leadId,
  leadName,
  color,
  stage,
  assignedProfileId,
}: Props): React.JSX.Element {
  // Only the columns AHEAD of the card's own. Whether the move asks for a reason
  // is the board's call (`requestMove`), the same rule a drag goes through.
  const boardMove = useBoardMove();
  const moveTargets = boardMove ? nextStages(stage, boardMove.stages) : [];
  const openEditModal = useCrmStore((s) => s.openEditModal);
  const updateLead = useUpdateLead();
  const [deleteOpen, setDeleteOpen] = useState(false);

  function setColor(next: PaletteColor | null): void {
    if (next === (color ?? null)) return;
    // The swatch is applied to the caches optimistically, so a failure has to say
    // so — otherwise the colour just slides back with no explanation.
    updateLead.mutate(
      { leadId, color: next },
      { onError: () => toast.error("No se pudo cambiar el color del lead.") },
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Stop pointer events from propagating to dnd-kit drag handler */}
          <button
            type="button"
            aria-label="Opciones del lead"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-600 focus:outline-none cursor-pointer"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {boardMove && moveTargets.length > 0 && (
            <>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <ArrowRight className="h-3.5 w-3.5" />
                  Mover a…
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {moveTargets.map((target) => (
                    <DropdownMenuItem
                      key={target.key}
                      onSelect={() => boardMove.requestMove(leadId, stage, target.key)}
                    >
                      {target.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
            </>
          )}
          {/* Renders nothing unless lead assignment is on and this user may act. */}
          <AssigneeSubMenu leadId={leadId} assignedProfileId={assignedProfileId} />
          <DropdownMenuItem
            onSelect={() => openEditModal(leadId)}
            className="gap-2"
          >
            <Pencil className="h-3.5 w-3.5" />
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setDeleteOpen(true)}
            className="gap-2 text-red-600 focus:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Eliminar
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] font-medium text-ink-500">
            Color
          </DropdownMenuLabel>
          <div className="flex items-center gap-1 px-2 py-1.5">
            {PIPELINE_COLOR_OPTIONS.map((c) => (
              <button
                key={c.name}
                type="button"
                aria-label={`Color ${c.name}`}
                onClick={() => setColor(c.name)}
                className={`h-5 w-5 rounded-full ${c.swatch} ${
                  color === c.name ? "ring-2 ring-ink-900 ring-offset-1" : ""
                }`}
              />
            ))}
            <button
              type="button"
              aria-label="Sin color"
              onClick={() => setColor(null)}
              className={`flex h-5 w-5 items-center justify-center rounded-full border border-ink-200 text-ink-500 hover:bg-ink-050 ${
                color ? "" : "ring-2 ring-ink-900 ring-offset-1"
              }`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mounted on demand — an always-mounted AlertDialog (and its useDeleteLead
          observer) per card is dead weight multiplied by the whole board. */}
      {deleteOpen && (
        <DeleteLeadDialog
          leadId={leadId}
          leadName={leadName}
          open
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}
